import { useActiveCamera } from "@/components/r3f/cameras/active-camera-context";
import { CameraSwitch } from "@/components/ui/camera-switch";
import {
  AutoClipCurrentViewIcon,
  ClippingBoxIcon,
  FaroTooltip,
  ResetIcon,
  Toolbar,
  ToolButton,
  ToolGroup,
  ZoomAllIcon,
} from "@faro-lotv/flat-ui";
import { Stack } from "@mui/material";
import type { JSX } from "react";

type ClippingBoxViewControlsProps = {
  /** If defined, a button lets the user turn the clipping box on and off */
  isBoxEnabled?: boolean;

  /** Called when the user turns the clipping box on or off */
  onBoxEnabledChange?(isEnabled: boolean): void;

  /** If defined, a button brings the clipping box back to its initial state */
  onResetBox?(): void;

  /** If defined, a button fits the clipping box to the current view */
  onFitBoxToView?(): void;

  /** If defined, a button lets the user bring the camera back to the default view */
  onRecenterView?(): void;

  /**
   * False to hide the camera projection and predefined views, e.g. in a 2D view
   *
   * @default true
   */
  showCameraSwitch?: boolean;
};

/** @returns the in-scene tools to toggle, reset or fit a clipping box, and the camera projection and predefined views */
export function ClippingBoxViewControls({
  isBoxEnabled = true,
  onBoxEnabledChange,
  onResetBox,
  onFitBoxToView,
  onRecenterView,
  showCameraSwitch = true,
}: ClippingBoxViewControlsProps): JSX.Element {
  const {
    cameraProjection,
    setCameraProjection,
    isProjectionSwitchVisible,
    predefinedView,
    setPredefinedView,
  } = useActiveCamera();
  const canEditBox = isBoxEnabled && (!!onResetBox || !!onFitBoxToView);

  return (
    <Stack
      // Use row-reverse to make the reset button appear on the right
      direction="row-reverse"
      sx={{
        justifyContent: "space-between",
        height: "100%",
        width: "100%",
      }}
    >
      <Stack sx={{ justifyContent: "center" }}>
        <Toolbar>
          {onBoxEnabledChange && (
            <ToolGroup>
              <FaroTooltip
                title={
                  isBoxEnabled
                    ? "Turn off the clipping box"
                    : "Turn on the clipping box"
                }
                placement="left"
              >
                <ToolButton
                  aria-label="clipping box"
                  aria-pressed={isBoxEnabled}
                  selected={isBoxEnabled}
                  onClick={() => onBoxEnabledChange(!isBoxEnabled)}
                >
                  <ClippingBoxIcon />
                </ToolButton>
              </FaroTooltip>
            </ToolGroup>
          )}
          {canEditBox && (
            <ToolGroup>
              {onResetBox && (
                <FaroTooltip
                  title="Reset clipping box"
                  placement="left"
                  aria-label="reset clipping box"
                >
                  <ToolButton onClick={onResetBox}>
                    <ResetIcon sx={{ width: "24px", height: "24px" }} />
                  </ToolButton>
                </FaroTooltip>
              )}
              {onFitBoxToView && (
                <FaroTooltip title="Auto-clip to current view">
                  <ToolButton onClick={onFitBoxToView}>
                    <AutoClipCurrentViewIcon />
                  </ToolButton>
                </FaroTooltip>
              )}
            </ToolGroup>
          )}
          {onRecenterView && (
            <ToolGroup>
              <FaroTooltip title="Recenter view" placement="left">
                <ToolButton aria-label="recenter view" onClick={onRecenterView}>
                  <ZoomAllIcon sx={{ width: "24px", height: "24px" }} />
                </ToolButton>
              </FaroTooltip>
            </ToolGroup>
          )}
        </Toolbar>
      </Stack>
      <Stack direction="column" spacing="1em" sx={{ justifyContent: "end" }}>
        {showCameraSwitch && isProjectionSwitchVisible && (
          <CameraSwitch
            cameraOptions3D={cameraProjection}
            onCameraChange={setCameraProjection}
            showPredefinedViewsOptions
            predefinedView={predefinedView}
            onPredefinedViewSelected={setPredefinedView}
          />
        )}
      </Stack>
    </Stack>
  );
}
